import { useState, useRef } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Undo2, Trash2 } from "lucide-react";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { copyText } from "@/lib/copyText";

const SHAPES: Record<string, string> = {
  Triangle: "polygon(50% 0%, 0% 100%, 100% 100%)",
  Trapezoid: "polygon(20% 0%, 80% 0%, 100% 100%, 0% 100%)",
  Parallelogram: "polygon(25% 0%, 100% 0%, 75% 100%, 0% 100%)",
  Pentagon: "polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)",
  Hexagon: "polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)",
  Octagon:
    "polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)",
  Star: "polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)",
  Cross:
    "polygon(35% 0%, 65% 0%, 65% 35%, 100% 35%, 100% 65%, 65% 65%, 65% 100%, 35% 100%, 35% 65%, 0% 65%, 0% 35%, 35% 35%)",
  Arrow:
    "polygon(0% 20%, 60% 20%, 60% 0%, 100% 50%, 60% 100%, 60% 80%, 0% 80%)",
  Diamond: "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)",
  Circle: "circle(50% at 50% 50%)",
  Ellipse: "ellipse(50% 40% at 50% 50%)",
  "Message Bubble":
    "polygon(0% 0%, 100% 0%, 100% 75%, 75% 75%, 75% 100%, 50% 75%, 0% 75%)",
  // Organic "blob" shapes -- currently one of the most common clip-path
  // trends in web design (replacing rigid rectangles with softer, more
  // human forms), verified directly before adding rather than assumed.
  // Achieved here with many polygon points at gentle, gradual radius
  // variation around a circle -- enough vertices that the eye reads it
  // as a smooth curve rather than an obviously many-sided polygon.
  "Blob 1":
    "polygon(88% 50%, 82% 73%, 60% 80%, 40% 82%, 20% 72%, 12% 50%, 25% 32%, 36% 8%, 62% 13%, 80% 29%)",
  "Blob 2":
    "polygon(94% 50%, 87% 71%, 73% 89%, 50% 91%, 31% 82%, 15% 70%, 12% 50%, 27% 37%, 30% 15%, 50% 8%, 70% 15%, 78% 34%)",
  // Diagonal section divider -- the angled-cut hero/section transition
  // common on modern landing and SaaS pages, achieved with a simple
  // 4-point polygon rather than an image or SVG background.
  "Diagonal Section": "polygon(0% 0%, 100% 0%, 100% 85%, 0% 100%)",
};

const TOOL_INFO_CONTENT = {
  what: `Generates CSS clip-path shapes — triangles, hexagons, arrows, organic blobs, and other non-rectangular shapes — visually, with the corresponding CSS code to copy into your project. Or build your own shape from scratch by clicking points onto a canvas.`,
  how: `Choose a shape from the presets, preview it applied to a sample element, and copy the generated CSS clip-path code. Or switch to "Build Your Own" and click to place points around the outline of a custom shape — at least 3 points — to generate its clip-path automatically.`,
  tip: `Clip-path shapes crop an element rather than resizing it, so make sure your underlying image or content is large enough that the important parts survive being cropped into the shape you choose. The Blob shapes and Diagonal Section are currently common in web design for softer, less rigid-looking layouts than a plain rectangle. When building a custom shape, place points in order around the outline (clockwise or counter-clockwise) rather than jumping across the shape, or the polygon can end up with crossed, self-intersecting edges.`,
};

interface Point { x: number; y: number; }

export default function ClipPathGenerator() {
  const [mode, setMode] = useState<"preset" | "custom">("preset");
  const [shape, setShape] = useState("Hexagon");
  const [points, setPoints] = useState<Point[]>([]);
  const canvasRef = useRef<HTMLDivElement>(null);

  const presetCss = `clip-path: ${SHAPES[shape]};`;
  const customPolygon = points.length >= 3
    ? `polygon(${points.map(p => `${p.x.toFixed(1)}% ${p.y.toFixed(1)}%`).join(", ")})`
    : null;
  const customCss = customPolygon ? `clip-path: ${customPolygon};` : "";

  const css = mode === "preset" ? presetCss : customCss;

  const copy = () => {
    if (mode === "custom" && !customPolygon) {
      toast.error("Add at least 3 points to create a shape first.");
      return;
    }
    void copyText(css, "Copied to clipboard");
  };

  const addPoint = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    setPoints(prev => [...prev, { x, y }]);
  };

  const undoPoint = () => setPoints(prev => prev.slice(0, -1));
  const clearPoints = () => setPoints([]);

  // Was useLogToolUsage(customPolygon, undefined, { ownActions: true }) -- only true in custom mode,
  // missing preset mode entirely (where css = presetCss, always
  // non-empty). css itself is the real "has output" signal.
  const { blocked } = useLogToolUsage(css, undefined, { ownActions: true });


  return (
    <ToolContainer>
      <div className="flex gap-2 mb-6">
        <Button size="sm" variant={mode === "preset" ? "default" : "outline"} onClick={() => setMode("preset")}>
          Preset Shapes
        </Button>
        <Button size="sm" variant={mode === "custom" ? "default" : "outline"} onClick={() => setMode("custom")}>
          Build Your Own
        </Button>
      </div>

      {mode === "preset" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <div className="grid grid-cols-3 gap-2">
              {Object.keys(SHAPES).map(name => (
                <button
                  key={name}
                  type="button"
                  onClick={() => setShape(name)}
                  className={`px-3 py-2 rounded-lg border text-sm transition-colors ${
                    shape === name
                      ? "border-primary bg-primary/10"
                      : "border-border bg-card"
                  }`}
                >
                  {name}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-center">
            <div
              className="w-48 h-48 bg-gradient-to-br from-primary to-purple-500"
              style={{ clipPath: SHAPES[shape] }}
            />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <p className="text-sm font-medium mb-2">Click to place points</p>
            <p className="text-xs text-muted-foreground mb-2">
              Click anywhere in the box below to add a corner, in order, around the outline of the
              shape you want. Need at least 3 points to form a shape.
            </p>
            <div
              ref={canvasRef}
              onClick={addPoint}
              className="relative w-full aspect-square rounded-lg border-2 border-dashed border-border bg-card cursor-crosshair overflow-hidden"
              style={{
                backgroundImage: "linear-gradient(to right, var(--color-border) 1px, transparent 1px), linear-gradient(to bottom, var(--color-border) 1px, transparent 1px)",
                backgroundSize: "10% 10%",
              }}
            >
              <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
                {points.length >= 2 && (
                  <polyline
                    points={points.map(p => `${p.x},${p.y}`).join(" ")}
                    fill="none"
                    stroke="var(--signal)"
                    strokeWidth="0.6"
                  />
                )}
                {points.length >= 3 && (
                  <line
                    x1={points[points.length - 1].x}
                    y1={points[points.length - 1].y}
                    x2={points[0].x}
                    y2={points[0].y}
                    stroke="var(--signal)"
                    strokeWidth="0.4"
                    strokeDasharray="2,2"
                  />
                )}
                {points.map((p, i) => (
                  <circle key={i} cx={p.x} cy={p.y} r="1.4" fill="var(--signal)" />
                ))}
              </svg>
            </div>
            <div className="flex gap-2 mt-2">
              <Button size="sm" variant="outline" onClick={undoPoint} disabled={points.length === 0} className="gap-1.5">
                <Undo2 className="h-3.5 w-3.5" /> Undo Point
              </Button>
              <Button size="sm" variant="outline" onClick={clearPoints} disabled={points.length === 0} className="gap-1.5">
                <Trash2 className="h-3.5 w-3.5" /> Clear All
              </Button>
              <span className="text-xs text-muted-foreground self-center ml-1">{points.length} point{points.length === 1 ? "" : "s"}</span>
            </div>
          </div>
          <div className="flex items-center justify-center">
            {customPolygon ? (
              <div
                className="w-48 h-48 bg-gradient-to-br from-primary to-purple-500"
                style={{ clipPath: customPolygon }}
              />
            ) : (
              <p className="text-sm text-muted-foreground text-center px-6">
                Your shape's live preview appears here once you've placed at least 3 points.
              </p>
            )}
          </div>
        </div>
      )}

      <div className="mt-6">
        <div className="rounded-lg border bg-muted/30 p-4 font-mono text-sm break-all min-h-[3rem]">
          {blocked ? "— blocked at today's usage limit —" : (css || <span className="text-muted-foreground italic font-sans not-italic">Add at least 3 points above to generate CSS</span>)}
        </div>
        <Button className="mt-3" onClick={copy} disabled={blocked}>
          Copy CSS
        </Button>
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
